import {
  useConnectionStatusContext,
  useMarketAccountsContext,
  useMarketsContext,
  useSecuritiesContext,
  useSecurity,
  useUnifiedLiquidity,
  useUnifiedLiquidityContext,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import { usePermittedMarketAccountsSet } from '../../../hooks/usePermittedMarketAccountsSet';
import { useRestrictedTradingHiddenMarkets } from '../../../hooks/useRestrictedTradingHiddenMarkets';
import type { AvailabilityCondition, MarketSelectorItem } from '../types';
import { buildMarketSelectorItems } from '../utils/useBuildMarketSelectorItems';
import {
  getMarketDataMarketSelectorConditions,
  useMarketDataMarketSelectorConditions,
} from './useMarketDataMarketSelectorConditions';
import {
  getMarketDataMarketsAndAccounts,
  type UseMarketDataMarketsAndAccountsParams,
} from './useMarketDataMarketsAndAccounts';

interface UseMarketDataMarketSelectorItems extends UseMarketDataMarketsAndAccountsParams {
  queryMSSRecords?: boolean;
}

/**
 * Pulls together and builds the market selector items to be used by the market selector given some amount of use case
 * parameters.
 */
export const useMarketDataMarketSelectorItems = ({
  symbol,
  combineAllMarketAccounts,
  includeUnifiedLiquidityMarkets,
  queryMSSRecords,
  subAccountAllocations,
}: UseMarketDataMarketSelectorItems): MarketSelectorItem[] => {
  return useMarketDataMarketSelectorItemInventory({
    symbol,
    combineAllMarketAccounts,
    includeUnifiedLiquidityMarkets,
    queryMSSRecords,
    subAccountAllocations,
  }).items;
};

export const useMarketDataMarketSelectorItemInventory = ({
  symbol,
  combineAllMarketAccounts,
  includeUnifiedLiquidityMarkets,
  queryMSSRecords = true,
  subAccountAllocations,
}: UseMarketDataMarketSelectorItems) => {
  const ulResponse = useUnifiedLiquidity(includeUnifiedLiquidityMarkets ? symbol : undefined);
  const security = useSecurity(symbol);
  const buildMarketDataMarketSelectorItems = useMarketDataMarketSelectorItemBuilder();
  const permittedMarketAccountNames = usePermittedMarketAccountsSet({
    subAccountAllocations: subAccountAllocations ?? -1,
    combineAllMarketAccounts,
  });
  const { conditions, allConditionsReady } = useMarketDataMarketSelectorConditions({
    symbol,
    ulTokensForSymbol: ulResponse?.Tokens,
    queryMSSRecords,
  });

  const items = useMemo(
    () =>
      buildMarketDataMarketSelectorItems({
        symbol,
        combineAllMarketAccounts,
        includeUnifiedLiquidityMarkets,
        conditions,
        permittedMarketAccountNames,
        subAccountAllocations,
      }),
    [
      buildMarketDataMarketSelectorItems,
      symbol,
      combineAllMarketAccounts,
      includeUnifiedLiquidityMarkets,
      conditions,
      permittedMarketAccountNames,
      subAccountAllocations,
    ]
  );

  return { items, isReady: security !== undefined && allConditionsReady };
};

type BuildMarketDataMarketSelectorItemsParams = UseMarketDataMarketsAndAccountsParams & {
  conditions?: AvailabilityCondition[];
  permittedMarketAccountNames?: Set<string> | null;
};

/**
 * Returns the shared Market Data selector item builder.
 *
 * `useMarketDataMarketSelectorItems` passes hook-built conditions so it can subscribe to MSS when requested.
 * Dynamic callers, such as symbol-change defaulting, omit conditions and use the builder's baseline no-MSS conditions.
 */
export function useMarketDataMarketSelectorItemBuilder() {
  const { marketCredentialsWithConnectionStatusByMarketAccountName } = useConnectionStatusContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { marketAccountsByMarket } = useMarketAccountsContext();
  const { marketsByName } = useMarketsContext();
  const { unifiedLiquidityTokenBySymbol } = useUnifiedLiquidityContext();
  const hiddenMarkets = useRestrictedTradingHiddenMarkets();

  return useCallback(
    ({
      symbol,
      includeUnifiedLiquidityMarkets,
      conditions: inputConditions,
      permittedMarketAccountNames,
    }: BuildMarketDataMarketSelectorItemsParams): MarketSelectorItem[] => {
      const security = symbol ? securitiesBySymbol.get(symbol) : undefined;
      const ulResponse =
        includeUnifiedLiquidityMarkets && symbol ? unifiedLiquidityTokenBySymbol?.get(symbol) : undefined;
      const conditions =
        inputConditions ??
        getMarketDataMarketSelectorConditions({
          enableMSSValidation: false,
          symbol: security?.Symbol,
          ulTokensForSymbol: ulResponse?.Tokens,
          securitiesBySymbol,
          mssByAccountBySymbol: undefined,
          marketCredentialsWithConnectionStatusByMarketAccountName,
        });

      const { markets, marketAccounts } = getMarketDataMarketsAndAccounts({
        security,
        includeUnifiedLiquidityMarkets,
        ulResponse,
        marketAccountsByMarket,
        marketsByName,
        hiddenMarkets,
        securitiesBySymbol,
        permittedMarketAccountNames,
      });

      return buildMarketSelectorItems({ markets, marketAccounts, conditions, marketsByName });
    },
    [
      hiddenMarkets,
      marketAccountsByMarket,
      marketCredentialsWithConnectionStatusByMarketAccountName,
      marketsByName,
      securitiesBySymbol,
      unifiedLiquidityTokenBySymbol,
    ]
  );
}
